import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcTrfV1TrfSessionQueryServiceTrfSessionGet } from '@interxfi/api-client/generated/hooks/ixc-trf-v1-trf-session-query-service-trf-session-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import ClearIcon from '@interxfi/design-system/icons/react/icon-clear';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import PowerIcon from '@interxfi/design-system/icons/react/icon-power';
import UndoIcon from '@interxfi/design-system/icons/react/icon-undo';
import { type IxcTrfV1TrfSessionState, IxcTrfV1TrfSessionStatus } from '@interxfi/types';
import { TrfSessionSummaryBar } from '../../../../../../../../../../features/trf/session/summary-bar/trf-session-summary-bar';
import { TrfSessionConnectDialog } from '../../../../../../../../../../features/trf/session/trf-session-connect-dialog';
import { TrfSessionDisconnectDialog } from '../../../../../../../../../../features/trf/session/trf-session-disconnect-dialog';
import { TrfSessionEditDrawer } from '../../../../../../../../../../features/trf/session/trf-session-edit-drawer';
import { TrfSessionEventDrawer, TrfSessionEventSummary } from '../../../../../../../../../../features/trf/session/trf-session-event';
import { getTrfSessionStateListItems } from '../../../../../../../../../../features/trf/session/trf-session-list-items';
import { getTrfSessionDetailPath } from '../../../../../../../../../../features/trf/session/trf-session-routing';
import { TrfSessionSequenceResetDialog } from '../../../../../../../../../../features/trf/session/trf-session-sequence-reset-dialog';
import { TrfSessionStatusPill } from '../../../../../../../../../../features/trf/session/trf-session-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/trade/trf-session/_trf-session/$sessionId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `trf-session-${params.sessionId}`,
        label: 'TRF Session',
        to: getTrfSessionDetailPath(params.sessionId),
        suffix: (
          <CopyButton content={params.sessionId} label="Copy TRF Session ID" tooltip={{ copy: params.sessionId, copied: 'Copied TRF Session ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('TRF Session'),
  component: TrfSessionDetail,
});

function getListItems(data: IxcTrfV1TrfSessionState | undefined) {
  const listItems = getTrfSessionStateListItems(data);

  return {
    generalLeft: [
      listItems.statusPill,
      listItems.data.senderSeq,
      listItems.data.targetSeq,
      listItems.data.initiatorRunning,
      listItems.data.isLoggedOn,
    ] satisfies ReadonlyListItem[],
    generalRight: [
      listItems.sessionId,
      listItems.senderCompId,
      listItems.senderSubId,
      listItems.targetCompId,
      listItems.targetSubId,
    ] satisfies ReadonlyListItem[],
    config: [
      listItems.data.config.targetHost,
      listItems.data.config.targetPort,
      listItems.data.config.venue,
      listItems.data.config.mpid,
      listItems.data.config.heartbeatInterval,
    ] satisfies ReadonlyListItem[],
  };
}

function getMenuItems(sessionId: string, status: IxcTrfV1TrfSessionStatus | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: TrfSessionEventDrawer,
        props: { request: { sessionId: sessionId } },
      },
    },
    actions: [
      ...(status &&
      [
        IxcTrfV1TrfSessionStatus.LoggedOn,
        IxcTrfV1TrfSessionStatus.Connecting,
        IxcTrfV1TrfSessionStatus.Disconnected,
        IxcTrfV1TrfSessionStatus.Stopped,
      ].includes(status)
        ? [
            {
              id: 'edit',
              label: 'Edit TRF Session',
              icon: <EditIcon aria-hidden />,
              interaction: {
                type: 'drawer',
                drawer: {
                  Component: TrfSessionEditDrawer,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
            {
              id: 'resetSequence',
              label: 'Reset Sequence Numbers',
              icon: <UndoIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: TrfSessionSequenceResetDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(status && [IxcTrfV1TrfSessionStatus.Stopped, IxcTrfV1TrfSessionStatus.Connecting].includes(status)
        ? [
            {
              id: 'start',
              label: 'Start TRF Session',
              icon: <PowerIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: TrfSessionConnectDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(status && [IxcTrfV1TrfSessionStatus.LoggedOn, IxcTrfV1TrfSessionStatus.Connecting, IxcTrfV1TrfSessionStatus.Disconnected].includes(status)
        ? [
            {
              id: 'stop',
              label: 'Stop TRF Session',
              tone: 'danger-dk',
              icon: <ClearIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: TrfSessionDisconnectDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
    ],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load TRF session', 'view this TRF session');

function TrfSessionDetail() {
  const { sessionId } = Route.useParams();
  const request = { sessionId } as const;
  const { data, error, isPending } = useIxcTrfV1TrfSessionQueryServiceTrfSessionGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.trfSession), [data?.trfSession]);
  const menuItems = useMemo(() => getMenuItems(sessionId, data?.trfSession?.status), [sessionId, data?.trfSession?.status]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <TrfSessionStatusPill isPending={showPending} size="large" status={data?.trfSession?.status} />
            </HorizontalStack>
            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <TrfSessionSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <TrfSessionEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.generalLeft} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.generalRight} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Config</Typography.Level3>

          <AppReadonlyList isPending={showPending} items={listItems.config} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
